import { LEAD_OVERALL_SCORE } from "@signalist/shared-types";

/**
 * Shared score → tier mapping for the beautified scoring hovers (PREC-3499).
 * Mirrors the colour mapping of `renderCurrentScoreResult` (List columns) and
 * `ProspectScoreBadge`, using semantic Tailwind tokens only so it is safe in the
 * Chrome extension's shadow root (no MUI / portal).
 */

/** The two fields of a persona result that decide whether it reads "?". */
interface PersonaVerdict {
	overallScore?: string | null;
	result?: string | null;
}

/**
 * Whether a lead's persona result renders as "?": scoring ran and could not judge the lead.
 *
 * Decided on the stored verdict first. `overallScore` "Unsure" is what the engine writes for a
 * segment × persona cell with no criteria, a company it could not classify and an investor no
 * persona accepts (PREC-4074, PREC-4075), and those rows keep the real persona as their `result`,
 * so a check on `result` alone let them fall through to "Still ongoing". A persona classifier that
 * answered "Unsure" is "?" too, unless the lead was disqualified: that is a verdict and stays DQ.
 */
export const isUnsureScore = (scoring?: PersonaVerdict | null): boolean =>
	scoring?.overallScore === LEAD_OVERALL_SCORE.UNSURE ||
	(scoring?.result === "Unsure" && scoring?.overallScore !== LEAD_OVERALL_SCORE.DISQUALIFIED);

/** Maps a raw `overallScore` / `companyScore` / `personaScore` value to a P-label. */
export const toScoreLabel = (rawScore?: string | null): string => {
	switch (rawScore) {
		case "1":
			return "P1";
		case "2":
			return "P2";
		case "3":
			return "P3";
		case "Disqualified":
			return "DQ";
		case "Unsure":
			return "?";
		default:
			return "?";
	}
};

/** A lead score in words, for text-only hovers: "Prio 1", "Disqualified", "?" or "Not set". */
export const toLeadScoreText = (rawScore?: string | null): string => {
	if (!rawScore) {
		return "Not set";
	}

	if (rawScore === LEAD_OVERALL_SCORE.DISQUALIFIED) {
		return "Disqualified";
	}

	// "?" is not a priority, and "Prio Unsure" read as one (PREC-4075).
	return rawScore === LEAD_OVERALL_SCORE.UNSURE ? "?" : `Prio ${rawScore}`;
};

/**
 * Pill / badge colour classes for a resolved label, matching the list pill.
 *
 * C1/C2/C3 (a score derived from company information alone) share the P tiers'
 * colours: the letter already says which kind of score it is, and the list pill
 * has always drawn both families the same way — giving the card its own palette
 * would make the same lead look like two different scores.
 */
export const prioBadgeClasses = (label: string): string => {
	switch (label) {
		case "P1":
		case "C1":
			return "text-text-success bg-background-success";
		case "P2":
		case "C2":
			return "text-text-warning bg-background-warning";
		case "P3":
		case "C3":
			return "text-error bg-error-light";
		case "DQ":
			return "text-white bg-error";
		default:
			return "text-text-meta bg-border-light";
	}
};

/** Sub-score badge colour by absolute points: green >= 3, amber 2, red <= 1. */
export const pointsBadgeClasses = (score: number): string => {
	if (score >= 3) {
		return "text-text-success bg-background-success";
	}

	if (score === 2) {
		return "text-text-warning bg-background-warning";
	}

	return "text-error bg-error-light";
};
